import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, Lock, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerLoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: PIN
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugPin, setDebugPin] = useState(null);

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/request-access`, { email });
      
      // If debug_pin is returned (testing mode), show it
      if (response.data.debug_pin) {
        setDebugPin(response.data.debug_pin);
      }
      
      toast.success("PIN sent! Check your email.");
      setStep(2);
    } catch (error) {
      console.error("Error requesting access:", error);
      toast.error("Failed to send PIN. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    if (!pin || pin.length !== 6) {
      toast.error("Please enter your 6-digit PIN");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/verify-pin`, { email, pin });
      
      // Store token and navigate to portal
      localStorage.setItem("customer_token", response.data.access_token);
      localStorage.setItem("customer_name", response.data.customer_name);
      localStorage.setItem("customer_email", response.data.email);
      
      toast.success(`Welcome back, ${response.data.customer_name}!`);
      navigate("/customer-portal");
    } catch (error) {
      console.error("Error verifying PIN:", error);
      toast.error("Invalid PIN. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-2xl font-bold text-primary font-montserrat">DC Tiling</h1>
          </Link>
          <h2 className="text-xl font-semibold text-gray-900 mt-4">Customer Portal</h2>
          <p className="text-gray-600 mt-2">
            Access your project details, payments, and messages
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {step === 1 ? (
            <form onSubmit={handleRequestAccess}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Enter Your Email</h3>
                <p className="text-sm text-gray-600 mt-1">
                  We'll send a secure PIN to access your portal
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                    data-testid="customer-login-email"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="customer-login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending PIN...
                    </>
                  ) : (
                    <>
                      Send PIN
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyPin}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Enter Your PIN</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Check your email for a 6-digit PIN
                </p>
              </div>

              {/* Debug PIN Display (for testing when SendGrid not configured) */}
              {debugPin && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Test Mode</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Your PIN: <span className="font-mono font-bold">{debugPin}</span>
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label>6-Digit PIN</Label>
                  <Input
                    type="text"
                    placeholder="000000"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="mt-1 text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="customer-login-pin"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading || pin.length !== 6}
                  data-testid="customer-verify-pin"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Access Portal
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setPin("");
                    setDebugPin(null);
                  }}
                  className="w-full text-sm text-gray-600 hover:text-primary"
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Help Text */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Don't have access?{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact us
            </Link>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Portal access is available for customers with an active quote or project.
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link to="/" className="text-sm text-gray-500 hover:text-primary">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
